import services from 'services/module';
import {Strings} from 'Strings';
import {playerUrl} from 'lazy_modules';
import {BackendSvc} from './BackendSvc';

export class PlayerSvc {
    private static Cache: {[id: string]: Player} = {};

    animatronImporter: any;
    anmPromise: () => ng.IPromise<Anm>;
    gwtPlayer = this.isGwt();
    fps       = parseInt(this.$location.search().fps, 10);

    constructor(private $location: angular.ILocationService,
                private $log: angular.ILogService,
                private $http: angular.IHttpService,
                private $filter: angular.IFilterService,
                private $q: ng.IQService,
                private BackendSvc: BackendSvc,
                private $ocLazyLoad: oc.ILazyLoad) {

        if (this.gwtPlayer) {
            this.anmPromise = _.once(() => $ocLazyLoad.load(this.$location.search().pUrl || 'https://player-dev.animatron.com/v2.0/player.js')
            // this.anmPromise = _.once(() => $ocLazyLoad.load('http://127.0.0.1:8888/playerDev/playerDev.nocache.js')
                .then(() => $q(resolve => {
                    const check = () => {
                        if (!window.animatron) {
                            setTimeout(() => check(), 50);
                            return;
                        }
                        const player    = window.animatron.Player.prototype;
                        player.isPaused = player.isPaused || player.isStopped;
                        player.pause    = player.pause || player.stop;
                        resolve(window.animatron);
                    };
                    check();
                })));
        } else {
            this.anmPromise = _.once(() => $ocLazyLoad.load(playerUrl()).then(() => {
                const anm = window.anm;
                this.decoratePlayer(anm);
                this.animatronImporter = new anm.importers.animatron();
                return anm;
            }));
        }
    }

    getAnm(func: Function) {
        return this.anmPromise().then(anm => func(anm));
    }

    resetPlayer = (playerPromise: Player | ng.IPromise<Player>) => this.$q((resolve: ng.IQResolveReject<void>, reject: ng.IQResolveReject<any>) => {
        const reset = (player: Player) => {
            try {
                this.$log.debug('Reseting player', player);
                if (player) {
                    if (player.invalidate) {
                        player.invalidate();
                        player.detach();
                    } else if (player.release) {
                        player.release();
                    }
                    delete PlayerSvc.Cache[player.id];
                }
                resolve();
            } catch (e) {
                // make it just resolve, since player throws a lot of errors during invalidation
                resolve();
                // this.$log.error(e);
                // reject();
            }
        };
        this.$q.resolve(playerPromise).then(reset);
    });

    resetPlayerById(canvasId: string) {
        this.resetPlayer(this.getPlayer(canvasId));
    }

    getPlayer(id: string): Player {
        return PlayerSvc.Cache[id];
    }

    instances(): Array<Player> {
        return _.values(PlayerSvc.Cache);
    }

    getJson(project: anm.Project | string | ExportJSON.JSON,
            params: anm.ProjectParams,
            forceAnonymous = false): ng.IPromise<ExportJSON.JSON> {
        if ((<ExportJSON.JSON>project).anim) return this.$q.resolve(project);
        return this.getProjectUrl(project).then((jsonUrl: string) => {
            if (params.width && params.height) {
                if (jsonUrl.indexOf('?') === -1) jsonUrl += '?';
                jsonUrl += '&w=' + params.width + '&h=' + params.height;
            }

            const needAuth = !forceAnonymous && !options.user.anonymous && !this.isCdnUrl(jsonUrl)
                && ((<anm.Project>project).isOwner || !(<anm.Project>project).editorReadAccess);
            const httpOpts: ng.IRequestShortcutConfig = {
                headers: needAuth ? options.headers : {}
            };

            const filteredUrl = this.$filter('removeUrlProtocol')(jsonUrl);
            return this.$http.get<ExportJSON.JSON>(filteredUrl, httpOpts).then(r => r.data);
        });
    }

    createPlayer(project: anm.Project | string | ExportJSON.JSON,
                 divId: string,
                 params: anm.ProjectParams,
                 callback?: (player: Player) => void,
                 callback2?: Function,
                 forceAnonymous = false): ng.IPromise<Player> {
        if (!project) throw new Error('Empty project was passed to createPlayer');

        const path = this.$location.path();

        params.width = params.width || (<anm.Project>project).width;
        params.height = params.height || (<anm.Project>project).height;
        if (this.fps) {
            params.fps = this.fps;
        }
        const promises: [ng.IPromise<Anm>, ng.IPromise<ExportJSON.JSON>] = [this.anmPromise(), this.getJson(project, params, forceAnonymous)];
        return this.$q.all<Anm, ExportJSON.JSON>(promises)
            .then(([anm, jsonObj]) => {
                if (this.$location.path() !== path) {
                    return this.$q.reject('Location has changed since create request issued');
                }
                return this.loadPlayer(jsonObj, divId, params, anm, callback, callback2);
            }).catch(e => {
                if (!/Location has changed/.test(e)) {
                    this.$log.error('Error creating player:', e);
                }
                return null;
            });
    }

    loadPlayer(jsonObj: ExportJSON.JSON,
               divId: string,
               params: anm.ProjectParams,
               anm: Anm,
               callback?: (Player: Player) => void,
               callback2?: Function): Player {
        if (jsonObj.anim.script && !this.gwtPlayer) {
            params = _.extend(params, {
                handleEvents: true, autoPlay: true, controlsEnabled: false, infiniteDuration: true
            });
        }

        const player: Player = anm.createPlayer(divId, params);

        const wrappedCallback = () => callback && callback(player);

        // if (PlayerSvc.Cache[divId]) throw new Error('Player is already attached to ' + divId);
        PlayerSvc.Cache[divId] = player;
        if (callback2) callback2();
        if (this.gwtPlayer) {
            (player as any).load(jsonObj, wrappedCallback);
            (player as any).setDebug(true);
        } else {
            player.load(jsonObj, this.animatronImporter, wrappedCallback);
        }
        this.checkAspectRatio(jsonObj.anim.dimension, params);
        return player;
    }

    protected isGwt() {
        return (this.$location.search().gwt || !options.isProduction) && !this.$location.search().old;
    }

    private checkAspectRatio([originalWidth, originalHeight]: number[], {width, height}: anm.ProjectParams) {
        const originalAspectRatio = originalWidth / originalHeight;
        const newAspectRatio = width / height;
        if (originalAspectRatio.toFixed(2) !== newAspectRatio.toFixed(2)) {
            this.$log.warn('Specified width & height is not correct',
                {originalAspectRatio, newAspectRatio, originalHeight, originalWidth, width, height}
            );
        }
    }

    private getProjectId = (project: anm.Project): any => project.id ? project.id : project;

    private getProjectBackendUrl = (projectId: string) =>
        this.BackendSvc.getProjectBackend(projectId).then(url =>
            url + '/project/export/json/' + projectId + '?'
        );

    public getProjectUrl = _.memoize((project: anm.Project | string) => this.$q((resolve): any => {
        if (typeof project === 'string') {
            resolve(project);
        } else if (!Strings.isBlank(project.cdnContentUrl) && project.cdnVersion === project.version && !project.needsRepublish) {
            resolve(project.cdnContentUrl + '?');
        } else if (Strings.isBlank(project.serverUrl)) {
            return this.getProjectBackendUrl(project.id);
        } else {
            if (this.isCdnUrl(project.serverUrl)) {
                resolve(project.serverUrl + '/' + project.id + '.json?');
            } else {
                resolve('//' + project.serverUrl.replace(':80', '') + '/project/export/json/' + project.id + '?');
            }
        }
    }), this.getProjectId);

    private isCdnUrl(url: string) {
        return url.indexOf('//clips.animatron') > -1 || url.indexOf('//publish.animatron') > -1;
    }

    // @formatter:off
    private decoratePlayer(anm: Anm) {
        const player = anm.Player.prototype;
        const happens = (p: Player) => p.happens || (p.state && p.state.happens) || anm.constants.STOPPED;
        player.isPlaying = player.isPlaying || function (this: Player) { return happens(this) === anm.constants.PLAYING; };
        player.isPaused = player.isPaused || function (this: Player) { return happens(this) === anm.constants.PAUSED; };
        player.isStopped = player.isStopped || function (this: Player) { return happens(this) === anm.constants.STOPPED; };
        player.getTime = player.getTime || function (this: Player) { return this.state.time; };
        player.setSize = player.setSize || function (this: Player, width: number, height: number) { return this._resize(width, height); };
    }
    // @formatter:on
}

class OldPlayer extends PlayerSvc {
    isGwt() {
        return false;
    }
}

// FIXME rename to PlayerSvc
services.service('Player', PlayerSvc);
services.service('PlayerSvc', PlayerSvc);
services.service('OldPlayer', OldPlayer);



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/PlayerSvc.ts